import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calculator, Search, Banknote, ShieldCheck, Truck, FileCheck, Star, Clock, MapPin } from "lucide-react";
import Counter from "./Counter";
import { CASH_COUNTING_IMG, CASH_FAN_IMG } from "../data/site";

export const SectionHeading = ({ eyebrow, title, sub, center }) => (
  <div className={`max-w-2xl ${center ? "mx-auto text-center" : ""}`}>
    {eyebrow && <span className="inline-block rounded-full bg-brand-mint px-3 py-1 text-xs font-bold uppercase tracking-wider text-brand-navy">{eyebrow}</span>}
    <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-brand-navy">{title}</h2>
    {sub && <p className="mt-3 text-brand-muted text-base sm:text-lg leading-relaxed">{sub}</p>}
  </div>
);

const STEPS = [
  { icon: Calculator, title: "Estimate", text: "Fill in the quick form or call us. We give you a free, no-obligation valuation based on real market conditions." },
  { icon: Search, title: "Inspect", text: "We come to you (or you drop by) for a ~10 minute inspection to confirm the car's condition and finalise the offer." },
  { icon: Banknote, title: "Cash", text: "Accept the offer and get paid on the spot by cash or EFT. We handle the paperwork and free removal.", img: CASH_COUNTING_IMG },
];

export const HowItWorks = () => (
  <section className="mx-auto max-w-7xl px-4 sm:px-6 py-20" data-testid="how-it-works-section">
    <SectionHeading center eyebrow="Three simple steps" title="How it works" sub="From first quote to cash in hand — usually the same day." />
    <div className="mt-12 grid gap-6 md:grid-cols-3">
      {STEPS.map((s, i) => {
        const hot = i === 2;
        return (
          <motion.div key={s.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.12 }}
            className={`group relative rounded-2xl border border-brand-line overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-all duration-300 ${hot ? "md:mt-8 bg-brand-navy text-white" : "bg-white p-8"}`} data-testid={`step-${i + 1}`}>
            {!hot && <span aria-hidden="true" className="absolute inset-0 z-0 origin-bottom scale-y-0 bg-brand-navy transition-transform duration-500 ease-out group-hover:scale-y-100" />}
            {hot && s.img && <div className="relative z-10 h-40 overflow-hidden"><img src={s.img} alt="Australian cash counted into a hand" loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /></div>}
            <div className={`relative z-10 ${hot ? "p-8 pt-6" : ""}`}>
              <span className={`absolute -top-4 left-8 flex h-9 w-9 items-center justify-center rounded-full font-display font-extrabold transition-colors duration-300 ${hot ? "bg-brand-gold text-brand-navy" : "bg-brand-navy text-white group-hover:bg-brand-gold group-hover:text-brand-navy"}`}>{i + 1}</span>
              <s.icon className={`h-9 w-9 transition-colors duration-300 ${hot ? "text-brand-gold" : "text-brand-navy group-hover:text-brand-gold"}`} />
              <h3 className={`mt-4 font-display text-xl font-extrabold transition-colors duration-300 ${hot ? "text-white" : "text-brand-navy group-hover:text-white"}`}>{s.title}</h3>
              <p className={`mt-2 text-sm leading-relaxed transition-colors duration-300 ${hot ? "text-white/75" : "text-brand-muted group-hover:text-white/85"}`}>{s.text}</p>
            </div>
          </motion.div>
        );
      })}
    </div>
  </section>
);

export const TrustSignals = ({ settings }) => {
  const s = settings || {};
  const stats = [
    { end: s.suburbs_served || 400, suffix: "+", label: "Suburbs served — Melbourne & Sydney" },
    { end: s.years_in_business || 15, suffix: "+ yrs", label: "Reliable & trusted" },
    { end: s.rating ? Math.round(s.rating * 10) / 10 : 4.9, suffix: "★", label: `${s.review_count || 380}+ reviews`, isFloat: true },
    { end: 10, suffix: " min", label: "Avg inspection" },
  ];
  return (
    <section className="relative noise-dark text-white overflow-hidden" data-testid="trust-signals">
      <img src={CASH_FAN_IMG} alt="" aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.07]" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 py-16 grid grid-cols-2 md:grid-cols-4 gap-8">
        {stats.map((st) => (
          <div key={st.label} className="text-center">
            <div className="font-display text-4xl sm:text-5xl font-black text-brand-gold">
              {st.isFloat ? `${st.end}${st.suffix}` : <Counter end={st.end} suffix={st.suffix} />}
            </div>
            <p className="mt-1 text-sm text-white/70 font-semibold">{st.label}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

const PROMISES = [
  { icon: ShieldCheck, title: "No obligation", text: "Free valuations with zero pressure. Our offer is honoured for 7 days." },
  { icon: FileCheck, title: "We handle the paperwork", text: "Transfer and finance payout all taken care of by our team." },
  { icon: Truck, title: "Free removal", text: "We tow it away free — driveable or not — across Melbourne & Victoria." },
  { icon: Banknote, title: "Paid on the spot", text: "Cash or EFT the moment you accept. No waiting for funds." },
];

export const WhyUs = () => (
  <section className="mx-auto max-w-7xl px-4 sm:px-6 py-20" data-testid="why-us">
    <SectionHeading eyebrow="Why sellers choose us" title="Straightforward, fair, and fast" sub="No tyre-kickers, no lowballing, no drawn-out private sale. Just a fair price and cash in hand." />
    <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
      {PROMISES.map((p) => (
        <div key={p.title} className="rounded-2xl border border-brand-line bg-white p-6 hover:-translate-y-1 hover:shadow-lg transition-all duration-200">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-softGold/40"><p.icon className="h-6 w-6 text-brand-navy" /></div>
          <h3 className="mt-4 font-display text-lg font-extrabold text-brand-navy">{p.title}</h3>
          <p className="mt-1 text-sm text-brand-muted leading-relaxed">{p.text}</p>
        </div>
      ))}
    </div>
  </section>
);

export const Reviews = ({ testimonials }) => {
  const items = (testimonials || []).slice(0, 6);
  if (!items.length) return null;
  return (
    <section className="bg-brand-surface" data-testid="reviews-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-20">
        <SectionHeading center eyebrow="Google reviews" title="What our sellers say" sub="Rated by hundreds of Victorians who sold their car the easy way." />
        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {items.map((t) => (
            <figure key={t.id} className="rounded-2xl border border-brand-line bg-white p-6" data-testid="review-card">
              <div className="flex gap-0.5">{Array.from({ length: t.rating || 5 }).map((_, i) => <Star key={i} className="h-4 w-4 fill-brand-gold text-brand-gold" />)}</div>
              <blockquote className="mt-3 text-brand-navy leading-relaxed">"{t.text}"</blockquote>
              <figcaption className="mt-4 flex items-center gap-2 text-sm">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-navy font-display font-bold text-white">{t.name?.[0]}</span>
                <span className="font-bold text-brand-navy">{t.name}</span>
                <span className="text-brand-muted flex items-center gap-1"><MapPin className="h-3 w-3" />{t.location}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
};

export const CTA = () => (
  <section className="mx-auto max-w-7xl px-4 sm:px-6 py-16">
    <div className="relative overflow-hidden rounded-3xl bg-brand-navy px-8 py-12 sm:px-14 sm:py-16 text-white flex flex-col md:flex-row md:items-center md:justify-between gap-6" data-testid="cta-banner">
      <img src={CASH_FAN_IMG} alt="" aria-hidden="true" className="pointer-events-none absolute -right-10 -bottom-16 w-2/3 max-w-md object-contain opacity-[0.09]" />
      <div className="relative">
        <h2 className="font-display text-3xl sm:text-4xl font-extrabold">Ready to turn your car into cash?</h2>
        <p className="mt-2 text-white/70 flex items-center gap-2"><Clock className="h-4 w-4 text-brand-gold" /> Free quote in minutes. Same-day payment available.</p>
      </div>
      <div className="relative flex flex-wrap gap-3">
        <Link to="/sell-your-car" className="rounded-full bg-brand-gold px-7 py-4 font-extrabold text-brand-navy hover:bg-brand-goldDark hover:-translate-y-0.5 transition-all" data-testid="cta-quote-btn">Get a price</Link>
        <a href="tel:0431206206" className="rounded-full border-2 border-white/30 px-7 py-4 font-bold text-white hover:bg-white/10 transition-colors" data-testid="cta-call-btn">Call 0431 206 206</a>
      </div>
    </div>
  </section>
);
